import { FormEvent, useState } from 'react';

import { verifyCognitoPassword } from '@/auth/utils/cognitoMfa';
import type { CognitoMfaType } from '@/auth/utils/cognitoSignIn';
import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import { factorPhrase } from './CognitoMfaFactorList';
import { type RunExclusive, mfaErrorMessage } from './mfaErrorMessage';

const MISSING_EMAIL =
  'We could not find the email address on your account. Reload the page and try again.';

interface CognitoMfaDisableFormProps {
  type: CognitoMfaType;
  /** The pool username. */
  email?: string;
  /**
   * Turns the factor off once the password is confirmed. It re-reads the
   * factors and the team policy first; a rejection means nothing changed.
   */
  onConfirmed: () => Promise<void>;
  onCancel: () => void;
  /** Holds the settings page's change lock from the password check onwards. */
  runExclusive: RunExclusive;
}

/** Turning a factor off asks for the password again first (ENG-6107). */
export default function CognitoMfaDisableForm({
  type,
  email,
  onConfirmed,
  onCancel,
  runExclusive,
}: CognitoMfaDisableFormProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // The pool's username is the email; without it the check cannot run.
    if (!email) {
      setError(MISSING_EMAIL);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await runExclusive(async () => {
        await verifyCognitoPassword(email, password);
        await onConfirmed();
      });
    } catch (err) {
      setError(mfaErrorMessage(err));
      setPassword('');
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-16" onSubmit={handleSubmit}>
      <p className="text-on-surface">
        To turn off {factorPhrase(type)} codes, re-enter your password.
      </p>
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!error}
        helperText={error}
        inputProps={{ autoComplete: 'current-password' }}
      />
      <div className="flex justify-end gap-8">
        <Button type="button" variant="tertiary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="destructive" disabled={!password || busy}>
          Turn off
        </Button>
      </div>
    </form>
  );
}
